/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import Link from 'next/link'
import { Insight } from 'src/components/insights/types'
import { Colors, Shapes, Typographies, useTypography } from 'src/components/theme'
import { TooltipProps, Typography } from '@mui/material'
import { ChevronRight } from '@emotion-icons/material/ChevronRight'
import { MobileElementProps } from 'src/components/theme/breakpoints'
import { useParsedRouter } from 'src/layout/hooks/use-parsed-router'
import { CircleIcon } from 'src/components/icons/circle-icon'
import { rgbaStrOpacity } from 'src/utils/colors'
import { LockedFeature } from 'src/components/locked-feature'
import { DarkTooltip } from 'src/components/tooltip/dark-tooltip'
import { EmotionIconProps } from '@emotion-icons/emotion-icon'
import { browserLogger } from 'src/utils/browser-logger'

type Props = {
  insight: Insight
  mobile?: boolean
  color?: string
  disabled?: boolean
}

type ComponentProps = {
  selected?: boolean
  available?: string
  color: string
  dashed?: boolean
}

type PathLinkProps = {
  to: string
  children?: React.ReactNode
  disabled?: boolean
}

const StyledLink = styled(Link)<Partial<PathLinkProps>>`
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}
`

export const PathLink = (props: PathLinkProps) => {
  const { to, children, disabled } = props
  const { query } = useParsedRouter()
  return (
    <StyledLink
      style={{
        pointerEvents: props.disabled ? 'none' : 'auto',
      }}
      href={{ pathname: to, query: query }}
      disabled={disabled}>
      {children}
    </StyledLink>
  )
}

export const PinIconBox = styled.div`
  display: none;
  margin-left: ${Shapes.spacing[2]}px;
  :hover {
    svg {
      color: ${Colors.textBlack};
    }
  }
`

export const InsightIconLink = styled.div<ComponentProps & MobileElementProps>`
  position: relative;
  text-decoration: none;
  padding: ${Shapes.spacing[1]}px ${(props) => (props.$mobile ? Shapes.spacing[2] : Shapes.spacing[4])}px;
  display: flex;
  align-items: center;
  background-color: ${(props: ComponentProps) => (props.selected ? rgbaStrOpacity(props.color, 0.1) : 'transparent')};
  cursor: ${(props: ComponentProps) => (props.available === 'true' ? 'pointer' : 'default')};

  &:hover {
    ${(props) => (!props.selected ? `background-color:${rgbaStrOpacity(props.color, 0.05)}` : undefined)};
    ${PinIconBox} {
      display: flex;
    }
    p {
      color: ${(props: ComponentProps) =>
        props.selected ? props.color : props.available === 'true' ? props.color : Colors.textLight} !important;
    }
  }
`

export const InsightIconBox = styled.div<ComponentProps & MobileElementProps>`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => (props.$mobile ? Shapes.insightIconBoxMobile : Shapes.insightIconBox)}px;
  height: ${(props) => (props.$mobile ? Shapes.insightIconBoxMobile : Shapes.insightIconBox)}px;
  margin-right: ${(props) => (props.$mobile ? Shapes.spacing[0] : Shapes.spacing[3])}px;
  border-radius: ${Shapes.borderRadius}px;
  background-color: ${(props) => (props.selected ? props.color : rgbaStrOpacity(props.color, 0.1))};
  border: 1px solid;
  border-color: ${(props: ComponentProps) =>
    props.selected ? props.color : props.available === 'true' ? rgbaStrOpacity(props.color, 0.5) : Colors.textLight};
  border-style: ${(props: ComponentProps) => (props.dashed ? 'dashed' : 'solid')};
  & svg {
    color: ${(props: ComponentProps) =>
      props.selected ? Colors.bgWhite : props.available === 'true' ? props.color : Colors.textLight};
    fill: ${(props: ComponentProps) =>
      props.selected ? Colors.bgWhite : props.available === 'true' ? props.color : Colors.textLight};
  }
`

export const InsightIconText = styled.div`
  display: flex;
  gap: ${Shapes.spacing[1]}px;
  align-items: center;
  flex-grow: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  ${useTypography(Typographies.text1m)}
  p,span {
    display: flex;
    align-items: center;
  }
`

export const ChevronRightIcon = styled(ChevronRight)`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props: ComponentProps) =>
    props.selected ? props.color : props.available ? Colors.textLight : Colors.textLight};
  width: ${Shapes.insightIconSize}px;
  height: ${Shapes.insightIconSize}px;
`

export const InsightIconBoxGoBox = styled.div`
  margin-left: ${Shapes.spacing[3]}px;
`

type InsightLinkLockerProps = {
  locked: boolean | undefined
  children: TooltipProps['children']
}
const InsightLinkLocker = (props: InsightLinkLockerProps) => {
  const { children, locked } = props

  return locked ? (
    <DarkTooltip placement="right" title="This feature is locked. Click to learn more.">
      {children}
    </DarkTooltip>
  ) : (
    <>{children}</>
  )
}

export const InsightIcon: React.FC<Props> = (props) => {
  const { insight, mobile, color = Colors.blueDark, disabled = false } = props
  const { router } = useParsedRouter()

  const currentPath = useMemo(() => (router.asPath.includes('owner-reveal') ? '/owner-reveal/' : '/'), [router.asPath])
  const icon = React.createElement(insight.icon as React.FC<EmotionIconProps>, {
    size: mobile ? Shapes.insightIconSizeMobile : Shapes.insightIconSize,
  })
  const nameIcon = insight.nameIcon
    ? React.createElement(insight.nameIcon, {
        size: Shapes.insightIconSize,
        color: 'white',
        style: { verticalAlign: 'sub' },
      })
    : null
  const nameIconColor = insight.nameIconColor

  const available = insight.available || false
  const pathname = `${currentPath}insights/${insight.id}`
  const selected = !!router.query.url && router.query.url[1] === insight.id
  const textColor = selected ? color : insight.available ? Colors.neutral500 : Colors.neutral300
  const circleIconColor = nameIconColor || Colors.blueDark

  const onHover = () => {
    browserLogger.debug(`Hovering over insight tab: ${insight.id}`)
    try {
      window.CommandBar?.trackEvent(insight.id)
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <PathLink disabled={!available || disabled} to={selected ? `/` : pathname}>
      <InsightLinkLocker locked={insight.locked}>
        <InsightIconLink
          onMouseEnter={onHover}
          color={color}
          selected={selected}
          available={available.toString()}
          $mobile={mobile ? true : undefined}>
          <InsightIconBox
            color={color}
            selected={selected}
            available={available.toString()}
            $mobile={mobile ? true : undefined}>
            {icon}
          </InsightIconBox>
          {!mobile && (
            <InsightIconText id={insight.id + '-tab'}>
              <Typography color={textColor} variant="textmdm">
                {insight.name}
                {nameIcon && (
                  <CircleIcon
                    style={{
                      marginLeft: Shapes.spacing[1],
                    }}
                    size={Shapes.insightIconSize}
                    padding={4}
                    color={circleIconColor}>
                    {nameIcon}
                  </CircleIcon>
                )}
              </Typography>
              <LockedFeature isLocked={insight.locked} />
            </InsightIconText>
          )}
          {!mobile && (
            <InsightIconBoxGoBox>
              <ChevronRightIcon color={color} available={available.toString()} selected={selected} />
            </InsightIconBoxGoBox>
          )}
        </InsightIconLink>
      </InsightLinkLocker>
    </PathLink>
  )
}
